import React, { useState, useCallback } from 'react';
import { FormConfig } from '../types';
import { Card, FileUpload, SectionTitle } from './UIElements';

interface PaymentStepProps {
    formConfig: FormConfig;
    category?: string;
    email: string;
    name: string;
    phone: string;
    isSubmitting?: boolean;
    onPaymentComplete: (data: {
        method: 'online' | 'offline';
        reference: string;
        receipt: string | null;
        status: 'completed' | 'pending_verification';
    }) => void;
    content: { title: string; subtitle: string };
}

declare global {
    interface Window {
        FlutterwaveCheckout: any;
        PaystackPop: any;
    }
}

const PaymentStep: React.FC<PaymentStepProps> = ({
    formConfig,
    category,
    email,
    name,
    phone,
    isSubmitting,
    onPaymentComplete,
    content,
}) => {
    const [tab, setTab] = useState<'online' | 'offline'>('online');
    const [receipt, setReceipt] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');

    const fee = category === 'Fellow' ? 10000 : parseInt(formConfig.application_fee || '5000', 10);
    const gateway = formConfig.payment_gateway || 'flutterwave';
    const publicKey = formConfig.payment_public_key || '';
    const hasOnlinePayment = !!publicKey;

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);

    // Dynamically load a script
    const loadScript = useCallback((src: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load ${src}`));
            document.head.appendChild(script);
        });
    }, []);

    const handleFlutterwavePayment = async () => {
        setIsProcessing(true);
        setError('');
        try {
            await loadScript('https://checkout.flutterwave.com/v3.js');
            window.FlutterwaveCheckout({
                public_key: publicKey,
                tx_ref: `NIFST-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                amount: fee,
                currency: 'NGN',
                payment_options: 'card,banktransfer,ussd',
                customer: {
                    email: email,
                    name: name,
                    phone_number: phone,
                },
                customizations: {
                    title: 'NIFST Application Fee',
                    description: 'Membership application processing fee',
                },
                callback: (response: any) => {
                    if (response.status === 'successful' || response.status === 'completed') {
                        onPaymentComplete({
                            method: 'online',
                            reference: response.transaction_id?.toString() || response.tx_ref || '',
                            receipt: null,
                            status: 'completed',
                        });
                    } else {
                        setError('Payment was not successful. Please try again.');
                    }
                    setIsProcessing(false);
                },
                onclose: () => {
                    setIsProcessing(false);
                },
            });
        } catch (err) {
            setError('Failed to initialize payment. Please try again or use offline payment.');
            setIsProcessing(false);
        }
    };

    const handlePaystackPayment = async () => {
        setIsProcessing(true);
        setError('');
        try {
            await loadScript('https://js.paystack.co/v1/inline.js');
            const handler = window.PaystackPop.setup({
                key: publicKey,
                email: email,
                amount: fee * 100, // Paystack uses kobo
                currency: 'NGN',
                ref: `NIFST-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                metadata: {
                    custom_fields: [
                        { display_name: 'Name', variable_name: 'name', value: name },
                    ]
                },
                callback: (response: any) => {
                    onPaymentComplete({
                        method: 'online',
                        reference: response.reference || response.trxref || '',
                        receipt: null,
                        status: 'completed',
                    });
                    setIsProcessing(false);
                },
                onClose: () => {
                    setIsProcessing(false);
                },
            });
            handler.openIframe();
        } catch (err) {
            setError('Failed to initialize payment. Please try again or use offline payment.');
            setIsProcessing(false);
        }
    };

    const handleOnlinePayment = () => {
        if (gateway === 'paystack') {
            handlePaystackPayment();
        } else {
            handleFlutterwavePayment();
        }
    };

    const handleOfflineSubmit = () => {
        if (!receipt) {
            setError('Please upload your payment receipt before proceeding.');
            return;
        }
        setError('');
        onPaymentComplete({
            method: 'offline',
            reference: `OFFLINE-${Date.now()}`,
            receipt: receipt,
            status: 'pending_verification',
        });
    };

    return (
        <div className="space-y-8">
            <SectionTitle subtitle={content.subtitle}>{content.title}</SectionTitle>

            {/* Fee Summary */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                    <i className="fa-solid fa-receipt text-emerald-600 text-xl"></i>
                    <h4 className="text-lg font-bold text-emerald-800">Application Processing Fee</h4>
                </div>
                <p className="text-4xl font-extrabold text-emerald-700 my-4">{formatCurrency(fee)}</p>
                <p className="text-sm text-emerald-600">One-time non-refundable application fee</p>
            </div>

            {/* Payment Method Tabs */}
            <div className="flex rounded-xl overflow-hidden border border-slate-200">
                <button
                    type="button"
                    onClick={() => { setTab('online'); setError(''); }}
                    className={`flex-1 py-3 px-4 font-bold text-sm transition-all flex items-center justify-center gap-2 ${tab === 'online'
                        ? 'bg-[var(--primary-color)] text-white shadow-inner'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <i className="fa-solid fa-credit-card"></i>
                    Pay Online
                </button>
                <button
                    type="button"
                    onClick={() => { setTab('offline'); setError(''); }}
                    className={`flex-1 py-3 px-4 font-bold text-sm transition-all flex items-center justify-center gap-2 ${tab === 'offline'
                        ? 'bg-[var(--primary-color)] text-white shadow-inner'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <i className="fa-solid fa-building-columns"></i>
                    Pay Offline
                </button>
            </div>

            {/* Online Payment Tab */}
            {tab === 'online' && (
                <div className="space-y-4">
                    {hasOnlinePayment ? (
                        <div className="text-center space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-700">
                                    <i className="fa-solid fa-shield-halved mr-2"></i>
                                    Secure payment powered by <strong className="capitalize">{gateway}</strong>
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleOnlinePayment}
                                disabled={isProcessing}
                                className="w-full bg-[var(--primary-color)] hover:opacity-90 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all text-lg disabled:opacity-50"
                            >
                                {isProcessing ? (
                                    <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Processing...</>
                                ) : (
                                    <><i className="fa-solid fa-lock mr-2"></i>Pay {formatCurrency(fee)} Now</>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                            <i className="fa-solid fa-triangle-exclamation text-yellow-500 text-2xl mb-2"></i>
                            <p className="text-yellow-700 font-medium">Online payment is not currently available.</p>
                            <p className="text-yellow-600 text-sm mt-1">Please use the offline payment option instead.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Offline Payment Tab */}
            {tab === 'offline' && (
                <div className="space-y-6">
                    {/* Bank Details */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                        <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <i className="fa-solid fa-building-columns text-slate-500"></i>
                            Bank Transfer Details
                        </h4>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-slate-200">
                                <span className="text-sm text-slate-500">Bank Name</span>
                                <span className="font-bold text-slate-800">
                                    {formConfig.bank_name || 'Contact admin for details'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-slate-200">
                                <span className="text-sm text-slate-500">Account Number</span>
                                <span className="font-bold text-slate-800 font-mono tracking-wider">
                                    {formConfig.bank_account_number || '—'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-slate-500">Account Name</span>
                                <span className="font-bold text-slate-800">
                                    {formConfig.bank_account_name || '—'}
                                </span>
                            </div>
                        </div>
                        <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <p className="text-xs text-orange-700">
                                <i className="fa-solid fa-info-circle mr-1"></i>
                                Transfer exactly <strong>{formatCurrency(fee)}</strong> to the account above, then upload your receipt below.
                            </p>
                        </div>
                    </div>

                    {/* Receipt Upload */}
                    <FileUpload
                        label="Payment Receipt / Screenshot"
                        accept="image/*"
                        value={receipt}
                        onUpload={(v) => setReceipt(v || null)}
                        required
                    />

                    <button
                        type="button"
                        onClick={handleOfflineSubmit}
                        disabled={isSubmitting}
                        className="w-full bg-[var(--secondary-color)] hover:opacity-90 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all text-lg disabled:opacity-50"
                    >
                        {isSubmitting ? (
                            <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Submitting... Please wait</>
                        ) : (
                            <><i className="fa-solid fa-upload mr-2"></i>Submit Receipt & Complete Application</>
                        )}
                    </button>

                    <p className="text-xs text-slate-500 text-center">
                        <i className="fa-solid fa-clock mr-1"></i>
                        Offline payments are subject to manual verification. You will receive confirmation once approved.
                    </p>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-red-600 font-medium text-sm">
                        <i className="fa-solid fa-circle-exclamation mr-2"></i>{error}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PaymentStep;
